import React from "react";
import { isChunkLoadError, reloadForChunk } from "../lib/chunkReload.js";

/** Catch a render crash and say so, instead of unmounting to a white page.
 *
 *  React 18 tears the whole tree down when a render throws and nothing catches
 *  it, leaving an empty `#root` and a console message the user will never see.
 *  That is the blank screen, and it is the least useful failure the app has:
 *  no information for the person looking at it, none for anyone they report it
 *  to.
 *
 *  The inline watchdog in `index.html` is the backstop and covers what this
 *  cannot — a bundle that fails to download or parse, where no React exists to
 *  do the catching. This one covers the commoner case and covers it better:
 *  it fires immediately rather than after the watchdog's eight seconds, and it
 *  has the actual error and component stack rather than whatever `window
 *  .onerror` happened to see.
 *
 *  Reload, not "try again". Re-rendering the same tree from the same state
 *  reproduces the same crash, and a button that visibly does nothing is worse
 *  than no button. */

type Props = { children: React.ReactNode };
type State = { error: Error | null; stack: string | null };

export class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, stack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Console as well as screen: if this ever happens with devtools attached,
    // the stack should be there in full rather than truncated to fit a phone.
    console.error("[wov] render crash", error, info.componentStack);
    this.setState({ stack: info.componentStack ?? null });
    // A page's code went missing because WOV was redeployed while it was
    // open: reload once to the newest build (lib/chunkReload.ts).
    if (isChunkLoadError(error)) reloadForChunk();
  }

  render(): React.ReactNode {
    const { error, stack } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex min-h-[100dvh] items-center justify-center px-5 py-8">
        <div className="max-w-md text-center">
          {/* Inherit the document colour rather than naming one. index.html
              sets a readable colour for both light and dark on html/body/#root
              before any bundle runs, whereas `text-slate-900 dark:text-slate-50`
              needs the theme class to be on <html> — and a crash early enough
              to matter can happen before it is, which paints dark ink on a dark
              page. An error screen nobody can read is the bug it is reporting. */}
          {isChunkLoadError(error) ? (
            <>
              <p className="wov-t-lg font-semibold">A newer Waki Mail is out</p>
              <p className="mt-2 wov-t-sm leading-relaxed opacity-75">
                It was deployed while this one was open. Reload to switch to it. Nothing in your mailbox was touched.
              </p>
            </>
          ) : (
            <>
              <p className="wov-t-lg font-semibold">Waki Mail hit an error</p>
              <p className="mt-2 wov-t-sm leading-relaxed opacity-75">
                Something in the app failed, not your mailbox. Nothing has been sent, moved or deleted.
              </p>
            </>
          )}
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="btn-primary mt-5 min-h-[44px] rounded-xl px-6 wov-t-base font-semibold"
          >
            Reload
          </button>
          {/* Shown, not hidden behind a toggle: the whole point is that a
              screenshot of this page is enough to act on. */}
          <pre className="mt-5 max-h-56 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-black/[0.06] p-3 text-left wov-t-xs opacity-70 dark:bg-white/[0.08]">
            {error.message}
            {stack ? `\n${stack.trim().split("\n").slice(0, 6).join("\n")}` : ""}
          </pre>
        </div>
      </div>
    );
  }
}
